import React, { useState, useEffect, useRef } from 'react';
import {
  Flex,
  Box,
  useToast
} from '@chakra-ui/react';

import './App.css';
import { auth } from './Firebase';
// import { analytics } from './Firebase'; TODO unused for now

import Const from './Constants';

import {
  showSuccessToast,
  handleAuthStateChangeCallback,
  handleUpdateUserConfig
} from './Utils';

import { startProductTour } from './Onboarding/productTour';

import SideBar from './Components/SideBar';
import Notes from './Views/Notes';
import Tasks from './Views/Tasks';
import Interviews from './Views/Interviews';
import SignInView from './Views/SignInView';
import TaskDetailsBar from './Components/TaskDetailsBar';
import Meetings from './Views/Meetings';
import Feedback from './Views/Feedback';
import Profile from './Views/Profile';
import Admin from './Views/Admin';
import Agents from './Views/Agents';

function App() {
  const toast = useToast();

  const [appMode, setAppMode] = useState(null);
  const [userConfig, setUserConfig] = useState(null);
  // Always-current userConfig, so the onboarding-tour completion callback writes
  // against the freshest config (the tour mutates appMode as it runs).
  const userConfigRef = useRef(null);
  const [isSignedIn, setIsSignedIn] = useState(false); // Local signed-in state
  const [isAdmin, setIsAdmin] = useState(false);
  const [subscription, setSubscription] = useState({
    state: undefined,
    subscriptionId: null, // unused in front end for now
    timestamp: null // unused in front end for now
  });
  
  const [isSidebarOpen] = useState(true);
  // const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  
  // App level data context
  const [currentTask, setCurrentTask] = useState(null);

  const [interviewsData, setInterviewsData] = useState({
    global: [], // Global template
    local: [], // Local template
    interview: []
  });

  const [notesData, setNotesData] = useState([]);

  // Meetings and one-on-ones are owned by useMeetingsStore (see Stores/MeetingsStore.js).

  async function handleGetUser() {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const theURL = `${process.env.REACT_APP_BASE_URL}/user`;

      const response = await fetch(theURL, {
        headers: new Headers({
          Authorization: idToken
        })
      });
      const user = await response.json();
      return user;
    } catch (error) {
      console.error(error.message);
    }
  }

  async function loadUserConfig() {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const theURL = `${process.env.REACT_APP_BASE_URL}/user/config`;

      const response = await fetch(theURL, {
        headers: new Headers({
          Authorization: idToken
        })
      });
      const userConfig = await response.json();
      return userConfig;
    } catch (error) {
      console.error(error.message);
    }
  }

  async function onSignIn(signedIn) {
    if(signedIn) {
      const [uc, user] = await Promise.all([loadUserConfig(), handleGetUser()]);
      setUserConfig(uc);
      userConfigRef.current = uc;
      setIsAdmin(user.role === Const.sAdmin);

      // Tasks load on the Tasks view's own mount (see Views/Tasks.js), so no
      // pre-load here. Every tasks-store consumer lives inside the Tasks view.

      // First-run onboarding tour. `!== true` so users whose config predates the
      // flag (existing users) also see it once.
      const needsOnboarding = uc.local.onboardingCompleted !== true;

      // For first-run onboarding open directly on One on Ones (where the tour
      // begins) so the stored appMode (often Tasks) never flashes; otherwise
      // restore the user's last view.
      setAppMode(needsOnboarding ? Const.s1on1Meetings : uc.local.appMode);

      if(needsOnboarding) {
        setTimeout(() => {     // Small delay lets the shell mount before highlighting.
          startProductTour({
            setAppMode,
            onDone: () => handleUpdateUserConfig(
              auth,
              userConfigRef.current || uc,
              setUserConfig,
              { onboardingCompleted: true }
            )
          });
        }, 600);
      }
    } else {
      setAppMode(Const.sSignIn);
    }
  }

  // Simple routing
  useEffect(() => {
    async function handleSubscriptionCheck() {
      const user = await handleGetUser();

      setSubscription({
        state: user.subscription.state,
        subscriptionId: user.subscription.subscriptionId, // unused in front end for now
        timestamp: user.subscription.timestamp // unused in front end for now
      });

      if(user.subscription.state === true) {
        showSuccessToast(toast, `${Const.sAppName} Pro`, 'Thank you for subscribing!');
      }
    }

    // Take care of sign in
    if(!isSignedIn) {
      handleAuthStateChangeCallback(auth, isSignedIn, setIsSignedIn, onSignIn, setSubscription);
    }

    // Handle subscription logic
    if(window.location.pathname === '/lemon') {
      // We are back from a payment provider, check the subscription state and show a toast for the user
      setTimeout(() => {
        handleSubscriptionCheck();
      }, 2000); // We give it 2 sec so all initialization will be done before the check
    }

  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    userConfigRef.current = userConfig;
  }, [userConfig]);

  useEffect(() => {
    if(auth.currentUser && // Check if user is signed in
      userConfig && userConfig.local.appMode !== appMode) {
      handleUpdateUserConfig(auth, userConfig, setUserConfig, {
        appMode
      });
    }
  // eslint-disable-next-line
  }, [appMode]);

  function appModeToComponentMap(appMode) {
    if(appMode === Const.sNotes) {
      return <Notes
        auth={auth}
        userConfig={userConfig}
        setUserConfig={setUserConfig}
        notesData={notesData}
        setNotesData={setNotesData}
      />;
    }
    else if (appMode === Const.sTasks) {
      return <Tasks
        auth={auth}
        appMode={appMode}
        setAppMode={setAppMode}
        userConfig={userConfig}
        setUserConfig={setUserConfig}
        currentTask={currentTask}
        setCurrentTask={setCurrentTask}
      />
    }
    else if(appMode === Const.sInterviews) {
      return <Interviews
                auth={auth}
                userConfig={userConfig}
                setUserConfig={setUserConfig}
                interviewsData={interviewsData}
                setInterviewsData={setInterviewsData}
              />;
    }
    else if(appMode === Const.s1on1Meetings) {
      return <Meetings
                auth={auth}
                userConfig={userConfig}
                setUserConfig={setUserConfig}
                type={Const.s1on1s}
             />;
    }
    else if(appMode === Const.sMeetings) {
      return <Meetings
                auth={auth}
                userConfig={userConfig}
                setUserConfig={setUserConfig}
                type={Const.sMeetings}
             />;
    }
    else if(appMode === Const.sFeedback) {
      return <Feedback auth={auth} toast={toast}/>;
    }
    else if(appMode === Const.sProfile) {
      return <Profile
                auth={auth}
                setAppMode={setAppMode}
                setIsSignedIn={setIsSignedIn}
                subscription={subscription}
                setSubscription={setSubscription}
                toast={toast} />
    }
    else if(appMode === Const.sAgents) {
      return <Agents auth={auth} />;
    }
    else if(appMode === Const.sAdmin) {
      return <Admin auth={auth} />;
    }
    else {
      return null;
    }
  }

  return (
    <Box className='App'>
      {
      appMode === Const.sSignIn ?
      <SignInView
          auth={auth}
          setAppMode={setAppMode}
          isSignedIn={isSignedIn}
          setIsSignedIn={setIsSignedIn}
          setSubscription={setSubscription} />
      :
      <>
      <Flex width="100vw" overflow="hidden">
        {/* Sidebar + Main Content */}
        <Box flex="1" minW="0">
          <Flex>
            {isSidebarOpen && (
              <SideBar
                auth={auth}
                appMode={appMode}
                setAppMode={setAppMode}
                setIsSignedIn={setIsSignedIn}
                subscription={subscription}
                isAdmin={isAdmin}
              />
            )}
            {/* Main view */}
            <Box flex="1" minW="0">
              {appModeToComponentMap(appMode)}
            </Box>
          </Flex>
        </Box>

        {/* Right-side Task Details */}
        {currentTask !== null && appMode === Const.sTasks && (
          <Box
            w={Const.KTaskDetailsBarW}               // or whatever width you want
            minW={Const.KTaskDetailsBarW}
            maxW={Const.KTaskDetailsBarW}
            flexShrink="0"
            overflowY="auto"
            zIndex="10"
          >
            <TaskDetailsBar
              auth={auth}
              currentTask={currentTask}
              setCurrentTask={setCurrentTask}
              setAppMode={setAppMode}
            />
          </Box>
        )}
      </Flex>
      </>
      }
    </Box>
  );
}

export default App;
